/**
 * The localStorage handshakes other screens use to hand a game to PlayChess.
 *
 * Read-only `peekHandshakes()` is what the route switch uses to pick a flow;
 * the `clear*` helpers replicate PlayChess's own cleanup EXACTLY
 * (clearLeagueChallengeKeys L360-366, clearClubChallengeKeys L368-375,
 * `setLocalStorage('challengePacket', null)` L3899).
 *
 * Every read/write goes through `platform/storage` (M2 spec §5.1) so this
 * module names no web API: the app store behind it is localForage on web and
 * AsyncStorage on React Native. It is ASYNC either way — hence every function
 * here returns a promise.
 */
import {peekPendingAutoJoin} from '@/Utils/pendingAutoJoin';

import {getItem, removeItem, setItem} from './platform/storage';

export type LeagueHandshake = {
  oppId: string;
  leagueId: string;
  oppName: string;
};

export type ClubHandshake = {
  challengeId: string;
  role: string; // 'creator' | 'accepter'
  oppId: string;
  oppName: string;
  tc: string; // "M+S"
  returnId: string | null;
};

export type Handshakes = {
  /** written by ChallengeComponent before it navigates here (L373-378). */
  challengePacket: any | null;
  league: LeagueHandshake | null;
  club: ClubHandshake | null;
  /** dashboard "Play" quick action; peeked, never consumed, by the switch. */
  pendingAutoJoin: {control: string} | null;
};

export async function peekHandshakes(): Promise<Handshakes> {
  const [
    challengePacket,
    leagueOppId,
    leagueId,
    leagueOppName,
    clubChallengeId,
    clubRole,
    clubOppId,
    clubOppName,
    clubTc,
    clubReturnId,
  ] = await Promise.all([
    getItem('challengePacket'),
    getItem('league_opp_id'),
    getItem('league_id'),
    getItem('league_opp_name'),
    getItem('club_challenge_id'),
    getItem('club_challenge_role'),
    getItem('club_opp_id'),
    getItem('club_opp_name'),
    getItem('club_challenge_tc'),
    getItem('club_return_id'),
  ]);

  return {
    challengePacket: (challengePacket as any) ?? null,
    league: leagueOppId
      ? {
          oppId: String(leagueOppId),
          leagueId: leagueId ? String(leagueId) : '',
          oppName: leagueOppName ? String(leagueOppName) : '',
        }
      : null,
    club: clubChallengeId
      ? {
          challengeId: String(clubChallengeId),
          role: clubRole ? String(clubRole) : '',
          oppId: clubOppId ? String(clubOppId) : '',
          oppName: clubOppName ? String(clubOppName) : '',
          tc: clubTc ? String(clubTc) : '',
          returnId: clubReturnId ? String(clubReturnId) : null,
        }
      : null,
    pendingAutoJoin: peekPendingAutoJoin(),
  };
}

/** PlayChess L3899: the packet is nulled (not removed) once consumed. */
export async function consumeChallengePacket(): Promise<any> {
  const packet = await getItem('challengePacket');
  await setItem('challengePacket', null);
  return packet ?? null;
}

/** PlayChess L360-366 — all three keys, always together. */
export async function clearLeagueChallengeKeys(): Promise<void> {
  await removeItem('league_opp_id');
  await removeItem('league_id');
  await removeItem('league_opp_name');
}

/** PlayChess L368-375 — the five club keys (club_return_id survives). */
export async function clearClubChallengeKeys(): Promise<void> {
  await removeItem('club_challenge_id');
  await removeItem('club_opp_id');
  await removeItem('club_opp_name');
  await removeItem('club_challenge_tc');
  await removeItem('club_challenge_role');
}

/** PlayChess L3761-3767: consumed when the result modal closes. */
export async function consumeClubReturnId(): Promise<string | null> {
  const clubReturnId = await getItem('club_return_id');
  if (!clubReturnId) {
    return null;
  }
  await removeItem('club_return_id');
  return String(clubReturnId);
}
